import { useTheme, alpha, Box, Stack, Tooltip, Typography } from "@mui/material";
import LinearProgress, {
    linearProgressClasses,
} from "@mui/material/LinearProgress";
import { Business, Handshake, WarningAmber, ErrorOutline } from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const SectionName = ({ name, icon }) => {
    const theme = useTheme();

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            color={grey[600]}
            sx={{
                color: grey[600],
                ...theme.applyStyles("dark", { color: grey[500] }),
            }}
        >
            {icon}
            <Typography fontSize={16} fontWeight="500">
                {name}
            </Typography>
        </Stack>
    );
};

const Step = ({ Icon, title, description, active, completed, missingInfo, hasError, onClick }) => {
    const theme = useTheme();
    const stateColor = hasError ? "error.main" : active || completed ? "secondary.main" : grey[600];

    return (
        <Stack
            direction="row"
            gap={1}
            p={1}
            sx={{
                cursor: "pointer",
                borderRadius: 2,
                ":hover": {
                    ...(!active && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.secondary.main, 0.06),
                    }),
                },
                ...(active && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.secondary.main, 0.1),
                }),
            }}
            onClick={onClick}
        >
            <Tooltip title={title} arrow placement="right">
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        bgcolor: "background.paper",
                        boxSizing: "border-box",
                        borderRadius: "50%",
                        border: "1px solid",
                        borderColor: hasError ? "error.main" : active || completed ? "secondary.main" : grey[500],
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        ...theme.applyStyles("dark", {
                            borderColor: hasError ? "error.main" : active || completed ? "secondary.main" : grey[400],
                        }),
                    }}
                >
                    <Icon sx={{ fontSize: 22, color: stateColor }} />
                </Box>
            </Tooltip>
            <Stack gap={0.2} justifyContent="center">
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                        sx={{
                            color: "text.dark",
                            ...theme.applyStyles("dark", { color: "#fff" }),
                        }}
                    >
                        {title}
                    </Typography>
                    {hasError ? (
                        <Tooltip title="There's an error on this step — click to fix" arrow placement="top">
                            <ErrorOutline sx={{ fontSize: 16, color: "error.main" }} />
                        </Tooltip>
                    ) : missingInfo && (
                        <Tooltip title="Missing information — click to add" arrow placement="top">
                            <WarningAmber sx={{ fontSize: 16, color: "warning.main" }} />
                        </Tooltip>
                    )}
                </Stack>
                <Typography
                    variant="caption"
                    fontSize={11}
                    sx={{
                        color: grey[600],
                        ...theme.applyStyles("dark", { color: grey[400] }),
                    }}
                >
                    {description}
                </Typography>
            </Stack>
        </Stack>
    );
};

const StepperSidebar = ({
    activeStepId,
    steps = [],
    completedIdList = [],
    errorIdList = [],
    completedPercentage = 0,
    onClickStep,
}) => {
    const theme = useTheme();

    const section1Steps = steps.slice(0, 4);
    const section2Steps = steps.slice(4);

    return (
        <Stack gap={1.2}>
            <SectionName
                name="Business Identity & Platform Presence"
                icon={<Business fontSize="large" sx={{ color: "grey.400" }} />}
            />
            <Stack ml={2.5} gap={0.2}>
                {section1Steps.map((step) => (
                    <Step
                        key={step.title}
                        Icon={step.icon}
                        title={step.title}
                        description={step.description}
                        active={step.id === activeStepId}
                        completed={completedIdList.includes(step.id)}
                        missingInfo={step.id !== "lastStep" && !completedIdList.includes(step.id)}
                        hasError={errorIdList.includes(step.id)}
                        onClick={() => onClickStep(step)}
                    />
                ))}
            </Stack>
            <SectionName
                name="Organizational Focus & Impact"
                icon={<Handshake fontSize="large" sx={{ color: "grey.400" }} />}
            />
            <Stack ml={2.5} gap={0.2}>
                {section2Steps.map((step) => (
                    <Step
                        key={step.title}
                        Icon={step.icon}
                        title={step.title}
                        description={step.description}
                        active={step.id === activeStepId}
                        completed={completedIdList.includes(step.id)}
                        missingInfo={step.id !== "lastStep" && !completedIdList.includes(step.id)}
                        hasError={errorIdList.includes(step.id)}
                        onClick={() => onClickStep(step)}
                    />
                ))}
            </Stack>
        </Stack>
    );
};

export default StepperSidebar;
